// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { BADGES, earnedBadgeIds } from "../../lib/badges.js";
import { isDoubleTokenDay } from "../../lib/dailyQuestion.js";
import { t as ct } from "../../lib/content.js";
import { todayStr } from "../../lib/utils.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// Generic collapsible card used for the Home tab's three sections (Goals / Earn / Transaction
// History) — a tap-to-toggle header with a chevron, matching the pattern already used by the vault card.

// `open`/`onToggle` are optional and CONTROLLED — pass them together to drive the section
// from outside (e.g. the setup wizard's progressive reveal, Aug 2026); every other call site
// leaves them undefined and keeps the original uncontrolled defaultOpen behaviour untouched.
// variant="row" (R-C polish, Aug 2026): a flat list-row presentation for the Kids tab's
// grouped cards — the GROUP supplies the card chrome (bg/border/dividers), so each row
// renders bare: title line, one-line summary beneath while closed, content indented when
// open. Default variant is byte-identical to before; wizard/kid-mode call sites unaffected.
// `icon` (R-D polish, Aug 2026): optional tinted sprite icon rendered before the title,
// aria-hidden — colour without touching the title text any spec matches on.
// W3.3 tint pass v2 — the four kid-mode background families. High-transparency, white at
// the top (headers always read white), faint hue wash at the bottom. See CollapsibleSection.
// v2.1 (Alan, Aug 2026): the pure-white band shortened from the top third to the top 15%,
// so the wash starts earlier and the tint reads on shorter cards too — approved otherwise.
// Kid redesign (docs/DESIGN_GUIDANCE.md §3, Sep 2026): flat semantic tints, not gradients,
// and only for under-8s. The values are CSS variables set in src/styles/tokens.css under
// [data-mode="kid"][data-age="young"]; for 8+ they resolve to transparent, so the same card
// reads as plain white — navy + one pop is that register. The names are kept so the call
// sites don't move.
export const KID_TINT_GRADIENTS = {
  earning: "var(--section-tint-tokens, transparent)",
  learning: "var(--section-tint-play, transparent)",
  money: "var(--section-tint-tokens, transparent)",
  saving: "var(--section-tint-vault, transparent)",
  pig: "var(--section-tint-pig, transparent)",
};

export function CollapsibleSection({ title, defaultOpen = false, open: controlledOpen, onToggle, badge, summary, children, tone = "default", compactClosed = false, variant = "default", icon, testid, summaryInline, first }) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    const resolved = typeof next === "function" ? next(open) : next;
    if (isControlled) { if (onToggle) onToggle(resolved); }
    else setInternalOpen(resolved);
  };
  // R item 3 fix (Alan, Aug 2026): the wizard's progressive reveal (Wizard.jsx step 4 —
  // Daily habits → Earning activities → Goals) advances a per-section open map, which flows
  // down to this component as a CONTROLLED `open` prop (see Wizard.jsx's step-4 section
  // mounts, AdjustSections.jsx since C1) — but nothing was moving the viewport or focus to
  // match, so a section revealed below the fold (or announced to nothing, for a
  // screen-reader user) was easy to miss entirely after tapping Continue. Scoped to
  // `isControlled` specifically: this only ever fires inside the wizard's own driven flow,
  // never during ordinary Parent Mode Settings use, where `open` stays uncontrolled internal
  // state and this effect no-ops.
  const sectionRef = useRef(null);
  useEffect(() => {
    if (isControlled && open && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      sectionRef.current.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, open]);
  // R item 2 follow-up (Alan, Aug 2026): `tone="amber"` is opt-in, used ONLY by the Rewards
  // tab's "🎯 My Goals" section (ChildView.jsx) — every other CollapsibleSection (Earning
  // activities, Daily habits & streaks, every Settings section) doesn't pass `tone` at all,
  // so it keeps today's plain white/slate look unchanged. GoalCard itself (ChildHero.jsx)
  // stays white either way — the amber marks "this is the goals area", not each goal card.
  // W3.3 tint pass v2 (Alan, Aug 2026): the flat -50 fills from the first pass read too
  // strong side by side and clashed. Every family is now a high-transparency gradient that
  // stays WHITE for the top third — so every section's header row sits on white — and eases
  // into a faint wash of the family hue toward the bottom. Title and chevron are back to
  // neutral slate for the same reason. Hues anchor to docs/ui-icons.md's palette (gold
  // #E0A81C, teal #0F9B8E) where one exists. Retinting a family = one line below.
  // LB-163 (Sep 2026): tones are card families. "amber"/"money" → gold, "sky"/"learning"
  // → play, "saving" → teal, "pig" → pig; default (parent kid-detail rows, Settings) stays
  // the plain white card. Family = disc + title + left edge (tokens.css .kc-*).
  const family = { amber: "kc-gold", money: "kc-gold", earning: "kc-gold", sky: "kc-play", learning: "kc-play", saving: "kc-teal", pig: "kc-pig" }[tone] || null;
  const toneClass = family ? `kc ${family}` : "bg-white border border-slate-100";
  const toneTitleClass = family ? "kc-title" : tone === "danger" ? "text-rose-600" : "text-slate-700";
  // LB-164 (pass J): rows are taps — chevron and summary in ink on the row variant.
  const toneChevronClass = variant === "row" ? "text-slate-900" : "text-slate-400";
  // LB-164 (pass H): `variant="section"` is the composition form — a KidSectionHead on the
  // canvas with the children always shown beneath it. No box, no chevron, no collapse.
  // Title text and testids are unchanged so specs keyed on names keep matching.
  if (variant === "section") {
    return (
      <div className="mb-1 ks-section">
        <KidSectionHead family={family || "kc-gold"} icon={icon || "ic-star"} first={first}>{title}{summaryInline ? <span className="text-xs font-medium text-slate-500 ml-1.5">{summaryInline}</span> : null}</KidSectionHead>
        {children}
      </div>
    );
  }
  const containerClass = variant === "row"
    ? `px-4 py-3 ${!open ? "cursor-pointer" : ""}`
    : `${toneClass} rounded-2xl p-4 ${!open ? "cursor-pointer" : ""}`;
  const containerStyle = undefined;
  return (
    <div
      ref={sectionRef}
      tabIndex={-1}
      className={containerClass}
      style={containerStyle}
      onClick={() => { if (!open) setOpen(true); }}
    >
      {/* R-H: data-testid on the header button — the emoji that used to disambiguate these
          rows in specs is gone, and the summary lives OUTSIDE this button so it can't be
          matched by accessible name. Additive prop, only passed where specs need it. */}
      <button data-testid={testid} className="w-full flex items-center justify-between" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
        <span className={`${toneTitleClass} font-semibold text-sm flex items-center gap-1.5`}>
          {icon && <span className={family ? "kc-disc" : "flex-shrink-0 inline-flex"} aria-hidden="true">{icon}</span>}
          {title}
          {badge}
        </span>
        <span className={`${toneChevronClass} text-xs`}><Icon id={open ? "ic-chevron-up" : "ic-chevron-down"} size={14} className="inline-block" style={{ color: "currentColor" }} /></span>
      </button>
      {summary && !open && !compactClosed && <div className={variant === "row" ? "mt-0.5" : "mt-2"}>{summary}</div>}
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

// The Oink Shop — full-screen overlay, same fixed inset-0 pattern as VaultTransferOverlay.
// One equipped item per slot; buying auto-equips (see App.jsx's onBuyCosmetic); owned items
// can be worn/taken off freely. Everything here is fun-only by design — the Law-1 line at the
// bottom says so in the kid's own reading level.
//
// Aug 2026 characters/economy task: "Friends" is the first tab and the tab the shop opens on
// (characters are the headline, oinks-pricing-strategy.md §5) — a parallel catalog/ownership
// path from cosmetics (see App.jsx's onBuyCharacter/onEquipCharacter), rendered by the same
// grid/tile shape so the two feel like one shop rather than two bolted together. Every
// unaffordable item — character or cosmetic — is visible from day one, greyed, with a
// progress bar (never simply hidden): the shop doubles as a goals page. Every unowned item's
// thumbnail is a real, live render on the actual sprite (a genuine try-on, not a static
// icon), labelled "Preview" so it's never mistaken for already-equipped.
// ---- LB-248: Double token day banner (Option A, dismissable — Alan, 9 Sep) ----
// Marigold slab, navy ×2 coin, on Home and Earn for the day only. Dismissed per child per
// day on the device (localStorage), so it returns on the next double day and never nags
// the same day twice. WARM register: bolder than any other kid card because it happens once
// a month, but LOUD stays reserved for Money Monday.
export function DoubleTokenDayBanner({ child, settings }) {
  const today = todayStr();
  const key = `lb_dd_dismissed:${child?.id}`;
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(key) === today; } catch { return false; } });
  if (!child || dismissed || !isDoubleTokenDay(child.id, today, settings)) return null;
  return (
    <div data-testid="double-token-day" className="rounded-2xl p-3.5 mb-2 relative overflow-hidden" style={{ background: "var(--marigold, #FFC629)" }}>
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full" style={{ background: "rgba(255,255,255,.35)" }} aria-hidden="true" />
      <div className="relative flex items-center gap-3">
        <div className="w-14 h-14 rounded-full flex items-center justify-center font-extrabold text-xl flex-shrink-0" style={{ background: "var(--navy, #1B2A4A)", color: "var(--marigold, #FFC629)" }}>×2</div>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-lg leading-tight" style={{ color: "var(--navy, #1B2A4A)" }}>{ct("kid.double_day.title", "Double token day")}</div>
          <div className="text-sm font-semibold" style={{ color: "#5A4600" }}>{ct("kid.double_day.body", "Everything you earn today counts twice. Ends at bedtime.")}</div>
        </div>
        <button type="button" aria-label={ct("kid.double_day.dismiss", "Hide for today")} onClick={() => { try { localStorage.setItem(key, today); } catch {} setDismissed(true); }} className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(27,42,74,.12)", color: "var(--navy, #1B2A4A)" }}>
          <Icon id="ic-close" size={14} style={{ color: "currentColor" }} />
        </button>
      </div>
    </div>
  );
}

/* --------------------------- Trophy Cabinet --------------------------- */

// Badges + the mastered-words room. Earned badges come off profile.badges (stored, so pruned
// history never un-earns one — see lib/badges.js); locked ones render as greyed silhouettes
// with their hint always visible. "NEW" markers are a per-device nicety (localStorage), and
// get marked seen when the section is expanded. Deliberately NOT gated by oinksEnabled —
// trophies aren't currency; only the +10 OP unlock bonus is Oink-gated (in badges.js).

// LEARNING_BADGES: the eight earned by playing the games. The Learn tab passes
// scope="learning" so its trophies row is about learning; the Rewards mount keeps the full
// set, since money badges (Super Saver, Goal Getter) and streak badges belong there.
const LEARNING_BADGE_IDS = ["perfect_spelling", "perfect_vocab", "tt_full_run", "maths_100", "q_100", "q_500", "all_four_week", "words_25"];
export function TrophiesSection({ code, child, learning, scope = "all" }) {
  const shownBadges = scope === "learning" ? BADGES.filter((b) => LEARNING_BADGE_IDS.includes(b.id)) : BADGES;
  const [open, setOpen] = useState(false);
  const seenKey = `lb_badges_seen_${code}_${child.id}`;
  const earned = earnedBadgeIds(child);
  const masteredWords = ((learning?.vocabBank) || []).filter((v) => (v.correctCount || 0) >= 2).map((v) => v.word).sort((a, b) => a.localeCompare(b));
  const [wordsOpen, setWordsOpen] = useState(false);
  // "Seen" badges live in React state, read from localStorage only once on mount — NOT
  // re-read fresh on every render. That matters because toggleOpen() below writes to
  // localStorage synchronously; if `seen` were re-derived from localStorage on each render,
  // the very click that opens the section would already be reading its own just-written
  // value, and a badge's "NEW" flag would never survive to be seen even while the grid is
  // open. Marking happens on CLOSE (not open) for the same reason, in the other direction:
  // it lets the NEW flag stay visible for the section's entire open lifetime, only clearing
  // once the child has actually looked and moved on.
  const [seen, setSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(seenKey) || "[]"); } catch { return []; }
  });
  const newIds = [...earned].filter((id) => !seen.includes(id));

  function toggleOpen() {
    const next = !open;
    if (!next && newIds.length > 0) {
      const nextSeen = [...earned];
      setSeen(nextSeen);
      try { localStorage.setItem(seenKey, JSON.stringify(nextSeen)); } catch { /* storage full/blocked — NEW markers just persist */ }
    }
    setOpen(next);
  }

  return (
    <div data-testid="trophies-section" className={`kc-gold ${open ? "kc rounded-2xl p-4" : "kid-rows ks-line px-0.5"}`}>{/* LB-164: a row while closed; a card only while the cabinet is open */}
      <button className={`w-full flex items-center justify-between ${open ? "" : "kid-row"}`} onClick={toggleOpen} aria-expanded={open}>
        <span className="font-semibold text-sm text-slate-900 flex items-center gap-2"><Icon id="ic-trophy" size={16} hue="gold" />My Trophies{newIds.length > 0 && !open ? <span className="ml-2 text-xs font-bold bg-pink-500 text-white rounded px-2 py-0.5 align-middle">{ct("kid.new_chip", "New")}</span> : null}</span>
        <span className="flex items-center gap-2"><span data-testid="trophies-summary" className="text-xs text-slate-500 font-medium">{shownBadges.filter((b) => earned.has(b.id)).length}/{shownBadges.length} badges · {masteredWords.length} words mastered</span><span className="text-slate-400 text-xs"><Icon id={open ? "ic-chevron-up" : "ic-forward"} size={14} className="inline-block" style={{ color: "currentColor" }} /></span></span>
      </button>
      {open && (
        <div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {shownBadges.map((b) => {
              const isEarned = earned.has(b.id);
              const isNew = newIds.includes(b.id);
              return (
                <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-xl border p-2 text-center relative ${isNew ? "border-pink-400 bg-pink-50" : "border-slate-100"}`}>
                  {isNew && <span className="absolute -top-1.5 -right-1 text-xs font-bold bg-pink-500 text-white rounded px-1.5 py-0.5">{ct("kid.new_chip", "New")}</span>}
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center mx-auto ${isEarned ? "bg-marigold" : "bg-slate-200"}`} style={isEarned ? undefined : { opacity: 0.55 }}>
                    <Icon id={b.icon} size={22} style={{ color: isEarned ? "#1B2A4A" : "#8A93A6" }} />
                  </div>
                  <div className={`text-xs font-bold mt-1 ${isEarned ? "text-slate-700" : "text-slate-400"}`}>{b.label}</div>
                  <div className="text-xs text-slate-400 font-medium mt-0.5 leading-tight">{b.hint}</div>
                </div>
              );
            })}
          </div>
          <button className="w-full text-left mt-3 pt-3 border-t border-slate-100" onClick={() => setWordsOpen((w) => !w)} aria-expanded={wordsOpen}>
            <span className="font-bold text-xs text-slate-700 flex items-center gap-1.5"><Icon id="ic-vocab" size={14} hue="indigo" />Words I've Mastered</span>
            <span className="text-xs text-slate-500 font-medium ml-2">{masteredWords.length} and counting</span>
          </button>
          {wordsOpen && (
            masteredWords.length > 0 ? (
              <div className="mt-2">
                {masteredWords.map((w) => (
                  <span key={w} className="inline-block bg-slate-50 border border-slate-100 rounded px-2.5 py-1 text-xs font-medium text-slate-600 mr-1.5 mb-1.5">{w}</span>
                ))}
                <div className="text-xs font-medium text-slate-400 mt-1.5">Mastered words come back for a quick refresher in Today's Question — that's how you keep them.</div>
              </div>
            ) : (
              <div className="text-xs text-slate-400 font-medium mt-2">{ct("kid.vocab_master_hint", "Answer a vocab word right twice to master it — your collection starts there.")}</div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// LB-164: a section heading on the canvas — family disc + title, no box. The family class
// sets --fam / --fam-tint / --fam-ink from tokens.css, same as the cards.
// `first` (G.2, Alan): the first section under the hero sits close to it; every later
// section keeps the 20px of air above. Pass "md" to make it first only from tablet width.
export function KidSectionHead({ family, icon, children, first }) {
  // LB-244 (Alan's 8 Sep device pass, screenshots): sections sat ~28–30px apart and the first
  // one ~56px under the hero. One rule now — 16px above a head, which with the tabs' 8px
  // flex gap is the 24px section gap of DESIGN_GUIDANCE §6 — and the empty Home wrappers no
  // longer take a gap of their own (see .kid-home in tokens.css).
  const top = first === true ? "mt-1" : first === "md" ? "mt-4 md:mt-1" : "mt-4";
  return (
    <div className={`${family} ks-head flex items-center gap-2 ${top} mb-1.5 px-0.5`}>
      <span className="kc-disc" aria-hidden="true">{typeof icon === "string" ? <Icon id={icon} size={16} style={{ color: "currentColor" }} /> : icon}</span>
      <span className="kc-title font-extrabold text-sm">{children}</span>
    </div>
  );
}
